/**
 * @file     BookCallLink.tsx
 * @purpose  The one in-page "Book a call with Ray Rasouli" row for signed-in
 *           visitors who have the app (they get no pop-up). Buyer pages only,
 *           behind the prompt system flag; renders nothing otherwise.
 */
'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { featureFlags } from '@/core/config/featureFlags';
import { isBuyerPage } from '@/core/config/prompts';
import { routes } from '@/core/constants/routes';
import { RayAvatar } from './RayAvatar';
import { focusRing, promptColors } from './promptStyles';
import { usePromptSignals } from './usePromptSignals';

export function BookCallLink() {
  if (!featureFlags.promptSystem) return null;
  return <BookCallLinkInner />;
}

function BookCallLinkInner() {
  const { t } = useTranslation();
  const pathname = usePathname() ?? '';
  const signals = usePromptSignals({ immediate: true });
  if (!signals.loaded || !signals.signedIn || signals.hasApp !== 'yes' || !isBuyerPage(pathname)) return null;

  return (
    <Box
      component={Link}
      href={routes.meet}
      data-testid="book-call-link"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        minHeight: 64,
        py: 3,
        borderTop: `1px solid ${promptColors.borderSubtle}`,
        borderBottom: `1px solid ${promptColors.borderSubtle}`,
        color: promptColors.textPrimary,
        textDecoration: 'none',
        '&:hover': { color: promptColors.link, '& svg': { color: promptColors.link } },
        ...focusRing,
        '&:focus-visible': { ...focusRing['&:focus-visible'], outlineOffset: -2 },
      }}
    >
      <RayAvatar size={40} />
      <Box component="span" sx={{ flex: 1, minWidth: 0, fontSize: 15.5, fontWeight: 500, lineHeight: 1.3 }}>
        {t('prompts.offer.link', { defaultValue: 'Book a call with Ray Rasouli' })}
      </Box>
      <Box
        component="svg"
        aria-hidden="true"
        viewBox="0 0 24 24"
        sx={{ width: 16, height: 16, flexShrink: 0, color: promptColors.textMuted }}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M7 17L17 7M9 7h8v8" />
      </Box>
    </Box>
  );
}
